import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import {
  getDocumentPurchaseLines,
  getPurchaseLineFeatures,
  type PurchaseLineDTO,
  type PurchaseLineFeatures,
} from "../../api/documents";
import { getDocumentMPK } from "../../api/mpk";
import {
  applyOrderDimensions,
  getOrderMatchCandidates,
  getReceiptMatchCandidates,
  matchOrderLines,
  matchReceiptLines,
  type ApplyOrderDimensionsResult,
  type OrderMatchCandidate,
  type ReceiptMatchCandidate,
} from "../../api/purchaseMatching";
import { useAppTranslation } from "../../hooks/useAppTranslation";

type Props = {
  documentId: string;
  companyId: string;
  goodsInvoice: boolean;
  currency: string;
  embedded?: boolean;
  onMatchingApplied?: () => void;
};

type MatchingTab = "receipts" | "orders";

type PendingMatch = {
  tab: MatchingTab;
  ids: string[];
  matchingAlreadySaved: boolean;
  dimensionResult?: ApplyOrderDimensionsResult;
};

function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return "—";
  }

  return value.toLocaleString("pl-PL", {
    maximumFractionDigits: 4,
  });
}

function formatMoney(value: number | null | undefined, currency: string): string {
  if (value === null || value === undefined) {
    return "—";
  }

  return `${value.toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency}`;
}

function defaultMatchingTab(line: PurchaseLineDTO): MatchingTab {
  return line.lineType === "Item" ? "receipts" : "orders";
}

function statusChip(line: PurchaseLineDTO) {
  if (line.matchStatus === "Ready") {
    return <Chip size="small" color="success" label="Gotowe" />;
  }

  if (line.matchStatus === "Incomplete") {
    return <Chip size="small" color="warning" label="Niekompletne" />;
  }

  return <Chip size="small" variant="outlined" label="Brak dopasowania" />;
}

function getDimensionConfirmationText(
  result?: ApplyOrderDimensionsResult
): string {
  if (result?.currentSource === "template") {
    return "Wiersz ma już wymiary ustawione przez szablon. Kontynuowanie zastąpi je wymiarami z zamówienia.";
  }

  if (result?.currentSource === "order") {
    return "Wiersz ma już wymiary z poprzedniego dopasowania. Kontynuowanie zastąpi je wymiarami z nowego zamówienia.";
  }

  if (result?.currentSource === "manual") {
    return "Wiersz ma już wymiary ustawione ręcznie. Kontynuowanie zastąpi je wymiarami z zamówienia.";
  }

  return "Wiersz ma już ustawione wymiary. Kontynuowanie dopasowania zastąpi je wymiarami z zamówienia.";
}

export default function DocumentPurchaseMatchingSection({
  documentId,
  companyId,
  goodsInvoice,
  currency,
  embedded = false,
  onMatchingApplied,
}: Props) {
  const { t } = useAppTranslation("common");

  const [features, setFeatures] = useState<PurchaseLineFeatures | null>(null);
  const [rows, setRows] = useState<PurchaseLineDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [selectedLine, setSelectedLine] = useState<PurchaseLineDTO | null>(null);
  const [tab, setTab] = useState<MatchingTab>("orders");
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [orderCandidates, setOrderCandidates] = useState<OrderMatchCandidate[]>([]);
  const [receiptCandidates, setReceiptCandidates] =
    useState<ReceiptMatchCandidate[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [dimensionConfirmationOpen, setDimensionConfirmationOpen] =
    useState(false);
  const [pendingMatch, setPendingMatch] = useState<PendingMatch | null>(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setError(null);

      try {
        const featureFlags = await getPurchaseLineFeatures(companyId);

        if (!active) {
          return;
        }

        setFeatures(featureFlags);

        if (!featureFlags.usePurchOrderMatching || !goodsInvoice) {
          setRows([]);
          return;
        }

        setLoading(true);
        const lineRows = await getDocumentPurchaseLines(companyId, documentId);

        if (!active) {
          return;
        }

        setRows(lineRows);
      } catch (e) {
        console.error(e);

        if (!active) {
          return;
        }

        setError(
          e instanceof Error
            ? e.message
            : t("Nie udało się pobrać danych dopasowania ZZ/PZ.")
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      active = false;
    };
  }, [companyId, documentId, goodsInvoice, refreshKey, t]);

  useEffect(() => {
    let active = true;

    const loadCandidates = async () => {
      if (!selectedLine) {
        return;
      }

      setCandidateLoading(true);
      setDialogError(null);
      setSelectedIds([]);

      try {
        if (tab === "receipts") {
          const result = await getReceiptMatchCandidates(
            companyId,
            documentId,
            selectedLine.systemId
          );

          if (active) {
            setReceiptCandidates(result);
          }
        } else {
          const result = await getOrderMatchCandidates(
            companyId,
            documentId,
            selectedLine.systemId
          );

          if (active) {
            setOrderCandidates(result);
          }
        }
      } catch (e) {
        console.error(e);

        if (active) {
          setDialogError(
            e instanceof Error
              ? e.message
              : t("Nie udało się pobrać kandydatów do dopasowania.")
          );
        }
      } finally {
        if (active) {
          setCandidateLoading(false);
        }
      }
    };

    void loadCandidates();

    return () => {
      active = false;
    };
  }, [companyId, documentId, selectedLine, tab, t]);

  const currentCandidates = useMemo(() => {
    return tab === "receipts" ? receiptCandidates : orderCandidates;
  }, [orderCandidates, receiptCandidates, tab]);

  const openMatching = (line: PurchaseLineDTO) => {
    setSelectedLine(line);
    setTab(defaultMatchingTab(line));
    setSelectedIds([]);
    setOrderCandidates([]);
    setReceiptCandidates([]);
    setDialogError(null);
    setSuccessMessage(null);
  };

  const closeMatching = () => {
    if (saving) {
      return;
    }

    setSelectedLine(null);
    setSelectedIds([]);
    setDialogError(null);
  };

  const toggleCandidate = (id: string) => {
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );
  };

  const refreshAfterMatching = () => {
    setRefreshKey((current) => current + 1);
    onMatchingApplied?.();
  };

  const finishSuccessfulMatching = () => {
    setSuccessMessage(t("Dopasowanie ZZ/PZ zostało zapisane."));
    setSelectedLine(null);
    setSelectedIds([]);
    setPendingMatch(null);
    setDimensionConfirmationOpen(false);
    refreshAfterMatching();
  };

  const applyDimensionsAfterMatching = async (
    lineSystemId: string,
    force: boolean
  ): Promise<"done" | "confirmation" | "warning"> => {
    if (!features?.useLineDimensions) {
      return "done";
    }

    const result = await applyOrderDimensions(
      companyId,
      documentId,
      lineSystemId,
      force
    );

    if (result.needsConfirmation) {
      return "confirmation";
    }

    if (result.applied || result.reason === "lineDimensionsDisabled") {
      return "done";
    }

    if (result.reason === "headerDimensions") {
      setDialogError(
        t(
          "Dopasowanie zostało zapisane, ale wymiary z zamówienia nie mogą zostać zastosowane, ponieważ dokument używa wymiarów nagłówkowych."
        )
      );
      return "warning";
    }

    if (result.reason === "dimensionSplit") {
      setDialogError(
        t(
          "Dopasowanie zostało zapisane, ale wymiary nie mogą zostać zastosowane do wiersza z rozbiciem wymiarów."
        )
      );
      return "warning";
    }

    if (result.reason === "notMatched") {
      setDialogError(
        t(
          "Business Central nie rozpoznał zapisanego dopasowania przy przenoszeniu wymiarów. Odśwież dane i spróbuj ponownie."
        )
      );
      return "warning";
    }

    return "done";
  };

  const executeMatch = async (
    matchTab: MatchingTab,
    ids: string[],
    overwriteDimensions: boolean
  ) => {
    if (!selectedLine || ids.length === 0) {
      return;
    }

    setSaving(true);
    setDialogError(null);

    try {
      if (matchTab === "receipts") {
        await matchReceiptLines(
          companyId,
          documentId,
          selectedLine.systemId,
          ids
        );
      } else {
        await matchOrderLines(
          companyId,
          documentId,
          selectedLine.systemId,
          ids
        );
      }

      const dimensionState = await applyDimensionsAfterMatching(
        selectedLine.systemId,
        overwriteDimensions
      );

      if (dimensionState === "confirmation") {
        setPendingMatch({
          tab: matchTab,
          ids,
          matchingAlreadySaved: true,
        });
        setDimensionConfirmationOpen(true);
        refreshAfterMatching();
        return;
      }

      if (dimensionState === "warning") {
        refreshAfterMatching();
        return;
      }

      finishSuccessfulMatching();
    } catch (e) {
      console.error(e);
      setDialogError(
        e instanceof Error
          ? e.message
          : t("Nie udało się zapisać dopasowania ZZ/PZ.")
      );
    } finally {
      setSaving(false);
    }
  };

  const beginMatch = async () => {
    if (!selectedLine || selectedIds.length === 0) {
      return;
    }

    if (!features?.useLineDimensions || selectedLine.lineNo === null) {
      await executeMatch(tab, selectedIds, false);
      return;
    }

    setSaving(true);
    setDialogError(null);

    try {
      const dimensions = await getDocumentMPK(companyId, documentId);
      const lineDimensions = dimensions.filter(
        (item) => item.purchaseLineNo === selectedLine.lineNo
      );

      if (lineDimensions.length >= 2) {
        setDialogError(
          t(
            "Wiersz ma rozbicie na kilka zestawów wymiarów. Usuń rozbicie przed dopasowaniem ZZ/PZ."
          )
        );
        return;
      }

      if (lineDimensions.length === 1) {
        setPendingMatch({
          tab,
          ids: [...selectedIds],
          matchingAlreadySaved: false,
        });
        setDimensionConfirmationOpen(true);
        return;
      }
    } catch (e) {
      console.error(e);
      setDialogError(
        e instanceof Error
          ? e.message
          : t("Nie udało się sprawdzić aktualnych wymiarów wiersza.")
      );
      return;
    } finally {
      setSaving(false);
    }

    await executeMatch(tab, selectedIds, false);
  };

  const confirmDimensionOverwrite = async () => {
    if (!selectedLine || !pendingMatch) {
      return;
    }

    if (pendingMatch.matchingAlreadySaved) {
      setSaving(true);
      setDialogError(null);

      try {
        const result = await applyOrderDimensions(
          companyId,
          documentId,
          selectedLine.systemId,
          true
        );

        if (!result.applied) {
          setDialogError(
            t(
              "Dopasowanie zostało zapisane, ale Business Central nie zastosował wymiarów z zamówienia."
            )
          );
          setDimensionConfirmationOpen(false);
          setPendingMatch(null);
          refreshAfterMatching();
          return;
        }

        finishSuccessfulMatching();
      } catch (e) {
        console.error(e);
        setDialogError(
          e instanceof Error
            ? e.message
            : t("Nie udało się nadpisać wymiarów wiersza.")
        );
      } finally {
        setSaving(false);
      }

      return;
    }

    setDimensionConfirmationOpen(false);
    await executeMatch(pendingMatch.tab, pendingMatch.ids, true);
  };

  const cancelDimensionOverwrite = () => {
    if (pendingMatch?.matchingAlreadySaved) {
      setDialogError(
        t(
          "Dopasowanie zostało zapisane, ale istniejące wymiary pozostawiono bez zmian."
        )
      );
      refreshAfterMatching();
    }

    setDimensionConfirmationOpen(false);
    setPendingMatch(null);
  };

  if (!features?.usePurchOrderMatching || !goodsInvoice) {
    return null;
  }

  return (
    <>
      <Paper
        variant={embedded ? "elevation" : "outlined"}
        elevation={0}
        sx={embedded ? { p: 0, bgcolor: "transparent" } : { p: 2 }}
      >
        {!embedded && (
          <>
            <Typography variant="subtitle1">
              {t("Dopasuj wiersze faktury")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t(
                "Wybierz wiersz faktury, a następnie przypisz do niego odpowiednie zamówienie ZZ lub przyjęcie PZ."
              )}
            </Typography>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {embedded && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              "Wybierz wiersz faktury i użyj akcji Dopasuj, aby przypisać odpowiednie ZZ lub PZ."
            )}
          </Typography>
        )}

        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">{t("Pobieranie...")}</Typography>
          </Box>
        )}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t("Brak wierszy dostępnych do dopasowania.")}
          </Typography>
        )}

        {!loading && !error && rows.length > 0 && (
          <TableContainer
            sx={{
              overflowX: "auto",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Table size="small" sx={{ minWidth: 850 }}>
              <TableHead sx={{ backgroundColor: "action.hover" }}>
                <TableRow>
                  <TableCell>{t("Wiersz")}</TableCell>
                  <TableCell sx={{ minWidth: 260 }}>{t("Opis")}</TableCell>
                  <TableCell align="right">{t("Ilość")}</TableCell>
                  <TableCell align="right">{t("Dopasowano / ilość")}</TableCell>
                  <TableCell>{t("Status dopasowania")}</TableCell>
                  <TableCell align="right">{t("Akcja")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((line) => (
                  <TableRow
                    key={line.systemId}
                    hover
                    sx={{ "&:last-child td": { borderBottom: 0 } }}
                  >
                    <TableCell>{line.lineNo ?? "—"}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {line.description || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[line.lineType, line.no].filter(Boolean).join(" • ") ||
                          "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatNumber(line.quantity)}
                        {line.unitOfMeasureCode
                          ? ` ${line.unitOfMeasureCode}`
                          : ""}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={line.lineMatched ? 600 : 400}
                      >
                        {formatNumber(line.matchedQtyToInvoice)} / {formatNumber(line.quantity)}
                      </Typography>
                    </TableCell>
                    <TableCell>{statusChip(line)}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant={line.lineMatched ? "outlined" : "contained"}
                        onClick={() => openMatching(line)}
                        disabled={!line.matchable}
                      >
                        {line.lineMatched
                          ? t("Dodaj dopasowanie")
                          : t("Dopasuj")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {successMessage}
          </Alert>
        )}
      </Paper>

      <Dialog
        open={Boolean(selectedLine)}
        onClose={closeMatching}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{t("Dopasowanie ZZ/PZ")}</DialogTitle>
        <DialogContent dividers>
          {selectedLine && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  {t("Wiersz")} {selectedLine.lineNo ?? "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedLine.description || "—"}
                </Typography>
              </Box>

              <Tabs
                value={tab}
                onChange={(_, value: MatchingTab) => setTab(value)}
              >
                <Tab value="receipts" label={t("Przyjęcia PZ")} />
                <Tab value="orders" label={t("Zamówienia ZZ")} />
              </Tabs>

              {dialogError && <Alert severity="error">{dialogError}</Alert>}

              {candidateLoading && (
                <Box
                  sx={{
                    py: 3,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2">
                    {t("Pobieranie kandydatów...")}
                  </Typography>
                </Box>
              )}

              {!candidateLoading && currentCandidates.length === 0 && (
                <Alert severity="info">
                  {tab === "receipts"
                    ? t("Brak dostępnych przyjęć PZ do dopasowania.")
                    : t("Brak dostępnych zamówień ZZ do dopasowania.")}
                </Alert>
              )}

              {!candidateLoading && currentCandidates.length > 0 && (
                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 900 }}>
                    <TableHead sx={{ backgroundColor: "grey.100" }}>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell>
                          {tab === "receipts" ? t("PZ") : t("ZZ")}
                        </TableCell>
                        <TableCell>{t("Wiersz")}</TableCell>
                        <TableCell sx={{ minWidth: 260 }}>
                          {t("Opis")}
                        </TableCell>
                        <TableCell align="right">
                          {t("Do rozliczenia")}
                        </TableCell>
                        <TableCell align="right">
                          {t("Koszt jedn.")}
                        </TableCell>
                        <TableCell>{t("Status")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentCandidates.map((candidate) => {
                        const candidateId =
                          tab === "receipts"
                            ? (candidate as ReceiptMatchCandidate).receiptLineId
                            : (candidate as OrderMatchCandidate).orderLineId;
                        const alreadyMatched = candidate.alreadyMatched === true;
                        const orderCandidate =
                          candidate as OrderMatchCandidate;
                        const receiptCandidate =
                          candidate as ReceiptMatchCandidate;

                        const documentNo =
                          tab === "receipts"
                            ? receiptCandidate.receiptNo ||
                              receiptCandidate.orderNo ||
                              "—"
                            : orderCandidate.orderNo || "—";
                        const lineNo =
                          tab === "receipts"
                            ? receiptCandidate.receiptLineNo ??
                              receiptCandidate.orderLineNo ??
                              null
                            : orderCandidate.orderLineNo;
                        const qty =
                          candidate.qtyRcdNotInvoiced ??
                          candidate.qtyToInvoice ??
                          candidate.quantity ??
                          null;
                        const cost = candidate.directUnitCost ?? null;
                        const candidateCurrency =
                          candidate.currencyCode || currency;
                        const description = candidate.description || "—";
                        const uom = candidate.unitOfMeasureCode || "";

                        return (
                          <TableRow
                            key={`${tab}-${candidateId}`}
                            hover
                            selected={
                              alreadyMatched ||
                              selectedIds.includes(candidateId)
                            }
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={
                                  alreadyMatched ||
                                  selectedIds.includes(candidateId)
                                }
                                disabled={alreadyMatched || saving}
                                onChange={() => toggleCandidate(candidateId)}
                              />
                            </TableCell>
                            <TableCell>{documentNo}</TableCell>
                            <TableCell>{lineNo ?? "—"}</TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {description}
                              </Typography>
                              {uom && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {uom}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {formatNumber(qty)}
                            </TableCell>
                            <TableCell align="right">
                              {formatMoney(cost, candidateCurrency)}
                            </TableCell>
                            <TableCell>
                              <Stack
                                direction="row"
                                spacing={0.75}
                                flexWrap="wrap"
                                useFlexGap
                              >
                                {alreadyMatched && (
                                  <Chip
                                    size="small"
                                    color="success"
                                    label={t("Już dopasowane")}
                                  />
                                )}
                                {tab === "orders" &&
                                  !orderCandidate.hasPostedReceipt && (
                                    <Chip
                                      size="small"
                                      color="warning"
                                      label={t("Brak PZ")}
                                    />
                                  )}
                                {!alreadyMatched &&
                                  (tab !== "orders" ||
                                    orderCandidate.hasPostedReceipt) && (
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={t("Dostępne")}
                                    />
                                  )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {tab === "orders" &&
                orderCandidates.some(
                  (candidate) =>
                    selectedIds.includes(candidate.orderLineId) &&
                    !candidate.hasPostedReceipt
                ) && (
                  <Alert severity="warning">
                    {t(
                      "Wybrane zamówienie nie ma jeszcze zaksięgowanego PZ. Dopasowanie może zostać zapisane, ale faktura nie będzie gotowa do zatwierdzenia do czasu przyjęcia towaru."
                    )}
                  </Alert>
                )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMatching} disabled={saving}>
            {t("Anuluj")}
          </Button>
          <Button
            variant="contained"
            onClick={() => void beginMatch()}
            disabled={saving || candidateLoading || selectedIds.length === 0}
          >
            {saving ? t("Zapisywanie...") : t("Zapisz dopasowanie")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dimensionConfirmationOpen}
        onClose={() => {
          if (!saving) {
            cancelDimensionOverwrite();
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("Nadpisać wymiary wiersza?")}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            {t(getDimensionConfirmationText(pendingMatch?.dimensionResult))}
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t(
              "Jeżeli kontynuujesz, wymiary wiersza zostaną zastąpione wartościami wynikającymi z dopasowanego zamówienia."
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={cancelDimensionOverwrite}
            disabled={saving}
          >
            {pendingMatch?.matchingAlreadySaved
              ? t("Pozostaw obecne wymiary")
              : t("Anuluj")}
          </Button>
          <Button
            variant="contained"
            onClick={() => void confirmDimensionOverwrite()}
            disabled={saving}
          >
            {saving ? t("Zapisywanie...") : t("Nadpisz wymiary")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
